import { useId, type Ref } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CLOSE_FIELD_BOX, CLOSE_PHONE_FIELD_BOX } from "./closeLayout";
import { LEAVE_FIELD_ON_ARROWS } from "../listKeyboard";

// Клавиатурата — antd я няма.
const KeyboardIcon = () => (
  <svg width={18} height={14} viewBox="0 0 18 14" fill="none" aria-hidden>
    <rect x={0.8} y={0.8} width={16.4} height={12.4} rx={2.2} stroke="currentColor" strokeWidth={1.6} />
    <path
      d="M4 4.5h.01M7 4.5h.01M10 4.5h.01M13 4.5h.01M4 7h.01M7 7h.01M10 7h.01M13 7h.01M5.5 9.8h7"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
    />
  </svg>
);

/** Бутонът „Клавиатура" в полето за сума — оцветен, докато екранната е разпъната (за това поле). */
export function KeypadToggleButton({
  isOpen,
  onToggle,
  disabled,
}: {
  isOpen: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={t("ordering.keyboard")}
      aria-pressed={isOpen}
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => {
        // Кликът по бутона не е клик по реда (той само слага курсора в полето).
        event.stopPropagation();
        onToggle();
      }}
      className={`grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        isOpen ? "bg-pos-ready-bg text-pos-accent-ink" : "text-pos-muted hover:bg-pos-canvas hover:text-pos-ink2"
      }`}
    >
      <KeyboardIcon />
    </button>
  );
}

/**
 * „Сума дадена от клиента" в „Закрий" (дизайнът — бакшишът и рестото):
 * надписът с действие вдясно („Премахни ×" / „Изчисти ×"), полето с EUR и
 * бутона за екранната клавиатура (`useKeypadOpen`). Какво има под полето
 * (клавиатурата, банкнотите) решава панелът. На телефон полето е с размера на
 * методите и без бутона — там е системната клавиатура.
 */
export function GivenAmountField({
  label,
  value,
  placeholder,
  onChange,
  onNormalize,
  action = null,
  keypad,
  hasError = false,
  isCompact,
  isPhone = false,
  disabled,
  inputRef,
  leaveOnArrows = false,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (raw: string) => void;
  /** Излизане от полето / Enter: „55" → „55.00". */
  onNormalize: () => void;
  /** Бутонът вдясно от надписа; `null` — без него. */
  action?: { label: string; onClick: () => void } | null;
  keypad: { isOpen: boolean; toggle: () => void };
  hasError?: boolean;
  /** Таблет с двете колони — по-дребните надписи се събират на един ред. */
  isCompact: boolean;
  isPhone?: boolean;
  disabled: boolean;
  inputRef?: Ref<HTMLInputElement>;
  /** ↑ ↓ излизат от полето и местят метода вдясно (`listKeyboard.ts`) — в
   *  „Закрий" и „Нов депозит". */
  leaveOnArrows?: boolean;
}) {
  const inputId = useId();
  return (
    <div className="flex flex-col">
      <div className="flex h-5 items-center justify-between gap-2">
        <label
          htmlFor={inputId}
          className={`truncate font-bold text-pos-ink ${isCompact ? "text-[13px]" : "text-[14px]"}`}
        >
          {label}
        </label>
        {action && (
          <button
            type="button"
            disabled={disabled}
            onClick={action.onClick}
            className={`-mr-1 inline-flex h-5 shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-1 font-semibold leading-none text-pos-ink2 transition-colors hover:text-pos-danger-ink disabled:cursor-not-allowed disabled:opacity-50 ${
              isCompact ? "text-[12.5px]" : "text-[13.5px]"
            }`}
          >
            {action.label}
            <CloseOutlined className="text-[10px]" />
          </button>
        )}
      </div>

      <div
        className={`mt-2.5 flex items-center gap-3 border bg-pos-card ${isPhone ? CLOSE_PHONE_FIELD_BOX : CLOSE_FIELD_BOX} transition-[border-color,box-shadow] focus-within:border-pos-accent focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-pos-accent)_14%,transparent)] ${
          hasError
            ? "border-pos-danger-brd"
            : value !== ""
              ? "border-pos-accent shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-pos-accent)_14%,transparent)]"
              : "border-pos-line"
        }`}
      >
        <input
          ref={inputRef}
          {...(leaveOnArrows ? LEAVE_FIELD_ON_ARROWS : {})}
          id={inputId}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          // Разпъната клавиатура — системната не изскача на тъч екран.
          inputMode={keypad.isOpen && !isPhone ? "none" : "decimal"}
          autoComplete="off"
          onChange={(event) => onChange(event.target.value)}
          onFocus={(event) => event.target.select()}
          onBlur={onNormalize}
          onKeyDown={(event) => {
            // Enter само нормализира — закриването е с бутона (или Enter
            // извън полето), никога от полето за сумата.
            if (event.key === "Enter") {
              event.preventDefault();
              onNormalize();
            }
          }}
          // Телефон — 16px: под 16 iOS зуумва страницата при фокус.
          className={`h-full w-full min-w-0 flex-1 border-0 bg-transparent p-0 font-pos-mono font-bold text-pos-ink outline-none [font-variant-numeric:tabular-nums] placeholder:text-pos-faint disabled:cursor-not-allowed ${
            isPhone ? "text-[16px]" : "text-[22px]"
          }`}
        />
        <span className={`shrink-0 font-bold text-pos-muted ${isPhone ? "text-[13px]" : "text-[14px]"}`}>EUR</span>
        {!isPhone && (
          <>
            <span aria-hidden className="h-7 w-px shrink-0 bg-pos-line2" />
            <KeypadToggleButton isOpen={keypad.isOpen} onToggle={keypad.toggle} disabled={disabled} />
          </>
        )}
      </div>
    </div>
  );
}
